<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

import { formatPrice } from '@/garage/format';
import { formatDateTime } from './format';
import { mountWithdrawForm, type WithdrawFormHandle } from '@/core/payments/widgets/withdraw-form';
import type { PayoutMethod, Withdrawal, WithdrawalLimits } from '@/core/payments/types';
import { api } from '@/net/api';

const loading = ref(true);
const error = ref<string | null>(null);
const limits = ref<WithdrawalLimits | null>(null);
const methods = ref<PayoutMethod[]>([]);
const history = ref<Withdrawal[]>([]);
const billingName = ref<{ first: string | null; last: string | null }>({ first: null, last: null });

const formEl = ref<HTMLElement | null>(null);
let formHandle: WithdrawFormHandle | null = null;

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  PROCESSING: 'Processing',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  FAILED: 'Failed',
};

function statusClass(status: string): string {
  if (status === 'APPROVED') return 'ok';
  if (status === 'REJECTED' || status === 'FAILED') return 'bad';
  return 'neutral';
}

async function load(): Promise<void> {
  loading.value = true;
  error.value = null;
  try {
    const [limitsRes, methodsRes, historyRes, billing] = await Promise.all([
      api.withdrawalLimits(),
      api.paymentMethods(),
      api.withdrawals(),
      api.billingIdentity().catch(() => null),
    ]);
    limits.value = limitsRes;
    methods.value = methodsRes.withdrawals.methods;
    history.value = historyRes;
    if (billing?.address) {
      billingName.value = {
        first: billing.address.firstName,
        last: billing.address.lastName,
      };
    }
    loading.value = false;
    mountForm();
  } catch {
    error.value = 'Could not load withdrawal information.';
    loading.value = false;
  }
}

function mountForm(): void {
  if (formHandle) { formHandle.destroy(); formHandle = null; }
  if (!formEl.value || !limits.value) return;
  formHandle = mountWithdrawForm(formEl.value, {
    methods: methods.value,
    limits: limits.value,
    firstName: billingName.value.first,
    lastName: billingName.value.last,
    submitWithdrawal: (body) => api.requestWithdrawal(body),
    onSubmitted: () => { api.withdrawals().then((rows) => { history.value = rows; }).catch(() => {}); },
  });
}

onMounted(() => { void load(); });
onUnmounted(() => { formHandle?.destroy(); formHandle = null; });
</script>

<template>
  <div class="pane" data-test="pane-withdrawals">
    <section class="block">
      <h3>Request withdrawal</h3>
      <p class="hint">
        Withdraw your balance to a card or bank account. Withdrawals are processed in EUR.
      </p>

      <p v-if="loading" class="empty">Loading…</p>
      <p v-if="error" class="empty error-text">{{ error }}</p>

      <div v-if="limits && !loading && !error" class="stats">
        <div class="stat">
          <span class="stat-label">Available</span>
          <span class="stat-value">{{ formatPrice(limits.availableCents, 'EUR') }}</span>
        </div>
        <div v-if="limits.feeBps > 0" class="stat">
          <span class="stat-label">Fee</span>
          <span class="stat-value">{{ (limits.feeBps / 100).toFixed(2) }}%</span>
        </div>
        <div class="stat">
          <span class="stat-label">Min / Max</span>
          <span class="stat-value">
            {{ formatPrice(limits.minCents, 'EUR') }} – {{ formatPrice(limits.maxCents, 'EUR') }}
          </span>
        </div>
      </div>

      <div ref="formEl" class="withdraw-form" data-test="withdraw-form"></div>
    </section>

    <section v-if="history.length > 0" class="block" data-test="withdrawal-history">
      <h3>Withdrawal history</h3>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Method</th>
              <th class="num">Amount</th>
              <th class="num">Fee</th>
              <th class="num">Net</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="w in history" :key="w.id" :data-withdrawal="w.id">
              <td class="when">{{ formatDateTime(w.createdAt) }}</td>
              <td>{{ w.method }}</td>
              <td class="num">{{ formatPrice(w.amountCents, w.currency) }}</td>
              <td class="num">{{ formatPrice(w.feeCents, w.currency) }}</td>
              <td class="num">{{ formatPrice(w.netCents, w.currency) }}</td>
              <td>
                <span class="badge" :class="statusClass(w.status)">
                  {{ STATUS_LABELS[w.status] ?? w.status }}
                </span>
                <span v-if="w.reason" class="reason">{{ w.reason }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<style scoped>
@import './pane.css';

.error-text {
  color: #ff8478;
}

.stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.7rem 0.9rem;
  border: 1px solid var(--steel-700);
  border-radius: 4px;
  background: var(--steel-900, #0f1318);
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 6rem;
}

.stat-label {
  color: var(--steel-400, #7c8896);
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.stat-value {
  color: var(--steel-200, #c8d1db);
  font-size: 0.88rem;
  font-variant-numeric: tabular-nums;
}

.withdraw-form {
  margin-top: 0.5rem;
}

/* The core widget renders via innerHTML with Tailwind utility classes, but this
   game does not load Tailwind. Override every layout and visual property here. */
.withdraw-form :deep(form) {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  max-width: 28rem;
}

.withdraw-form :deep([role="radiogroup"]) {
  display: flex;
  gap: 0.5rem;
}

.withdraw-form :deep([data-method]) {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;
  padding: 0.5rem 0.75rem;
  background: var(--steel-800);
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  color: var(--steel-200);
  font: inherit;
  font-size: 0.86rem;
  cursor: pointer;
  transition: border-color 120ms ease;
}

.withdraw-form :deep([data-method][aria-checked="true"]) {
  border-color: var(--accent);
  box-shadow: 0 0 8px rgba(232, 114, 44, 0.2);
}

.withdraw-form :deep([data-method] img) {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.withdraw-form :deep(form > .grid) {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.875rem;
}

.withdraw-form :deep(form label) {
  display: block;
  margin: 0;
  font-size: inherit;
  font-weight: normal;
  letter-spacing: normal;
  text-transform: none;
  color: inherit;
}

.withdraw-form :deep(form label > span:first-child) {
  display: block;
  margin-bottom: 0.35rem;
  color: #96a5b9;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.withdraw-form :deep(form input) {
  width: 100%;
  min-height: 44px;
  padding: 0.58rem 0.7rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background-color: var(--steel-900);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.4);
  color: var(--steel-200);
  font: inherit;
  font-size: 0.86rem;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}

.withdraw-form :deep(form input:hover) {
  border-color: var(--steel-400);
}

.withdraw-form :deep(form input:focus) {
  outline: none;
  border-color: var(--accent);
  box-shadow:
    inset 0 1px 2px rgba(0, 0, 0, 0.4),
    0 0 0 3px rgba(232, 114, 44, 0.18);
}

.withdraw-form :deep([data-error]) {
  display: block;
  margin-top: 0.25rem;
  color: #ff8478;
  font-size: 0.75rem;
}

.withdraw-form :deep([data-role="preview"]) {
  margin: 0;
  color: var(--steel-400);
  font-size: 0.82rem;
}

.withdraw-form :deep([data-role="error"]) {
  padding: 0.6rem 0.75rem;
  border: 1px solid rgba(231, 76, 60, 0.4);
  border-radius: 3px;
  background: rgba(231, 76, 60, 0.08);
  color: #ff8478;
  font-size: 0.82rem;
}

.withdraw-form :deep([data-role="ok"]) {
  padding: 0.6rem 0.75rem;
  border: 1px solid rgba(46, 204, 113, 0.4);
  border-radius: 3px;
  background: rgba(46, 204, 113, 0.08);
  color: #2ecc71;
  font-size: 0.82rem;
}

.withdraw-form :deep(button[type="submit"]) {
  width: 100%;
  min-height: 44px;
  padding: 0.65rem 1rem;
  border: 1px solid var(--accent);
  border-radius: 3px;
  background: var(--accent);
  color: #14181d;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
  transition: filter 120ms ease;
}

.withdraw-form :deep(button[type="submit"]:hover:not(:disabled)) {
  filter: brightness(1.08);
}

.withdraw-form :deep(button[type="submit"]:disabled) {
  opacity: 0.45;
  cursor: default;
}

.withdraw-form :deep(form > p),
.withdraw-form :deep(form > span) {
  margin: 0;
  color: var(--steel-400);
  font-size: 0.78rem;
}

.withdraw-form :deep(form label > span:last-child:not(:first-child):not([data-error])) {
  display: block;
  margin-top: 0.25rem;
  color: var(--steel-400);
  font-size: 0.72rem;
}

.when {
  white-space: nowrap;
}

.badge.neutral {
  background: rgba(99, 116, 138, 0.18);
  color: var(--steel-400);
}

.reason {
  display: block;
  margin-top: 0.2rem;
  color: var(--steel-400);
  font-size: 0.72rem;
}
</style>
